import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export type SelectionMode = 'select_all' | 'deselect_all';

export interface UseProviderSelectionReturn {
  selectionMode: SelectionMode;
  isSelected: (sku: string) => boolean;
  toggleProduct: (sku: string) => void;
  toggleMode: () => void;
  selectProducts: (skus: string[]) => void;
  deselectProducts: (skus: string[]) => void;
  saving: boolean;
  saveError: string | null;
  selectionCount: number;
  loaded: boolean;
}

interface PendingSave {
  merchantId: string;
  providerId: string;
  mode: SelectionMode;
  skipList: string[];
  allowedList: string[];
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!;
const SUPABASE_ANON_KEY = (
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_DEFAULT_KEY
)!;

/**
 * Flush a pending save using fetch with keepalive — works during page unload.
 * Falls back to supabase.rpc() if keepalive isn't needed.
 */
function flushSaveSync(pending: PendingSave, accessToken: string | null) {
  const url = `${SUPABASE_URL}/rest/v1/rpc/set_provider_selection`;
  const token = accessToken || SUPABASE_ANON_KEY;

  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        p_merchant_id: pending.merchantId,
        p_provider_id: pending.providerId,
        p_selection_mode: pending.mode,
        p_skip_list: pending.skipList,
        p_allowed_list: pending.allowedList,
      }),
      keepalive: true,
    });
  } catch {
    // Best-effort during unload — nothing we can do if it fails
  }
}

export const useProviderSelection = (
  merchantId: string | null,
  providerId: string | null,
): UseProviderSelectionReturn => {
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('select_all');
  const [skipList, setSkipList] = useState<Set<string>>(new Set());
  const [allowedList, setAllowedList] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const accessTokenRef = useRef<string | null>(null);

  // Keep the access token ref up to date for use in beforeunload
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      accessTokenRef.current = session?.access_token ?? null;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      accessTokenRef.current = session?.access_token ?? null;
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load current selection state from DB
  useEffect(() => {
    // Reset state when params change to avoid stale data
    setSelectionMode('select_all');
    setSkipList(new Set());
    setAllowedList(new Set());
    setLoaded(false);
    setSaveError(null);

    if (!merchantId || !providerId) {
      setLoaded(true);
      return;
    }

    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('merchant_provider_map')
          .select('capabilities')
          .eq('merchant_id', merchantId)
          .eq('provider_id', providerId)
          .single();

        if (error || !data) {
          setLoaded(true);
          return;
        }

        const caps = data.capabilities as any;
        if (caps?.selection_mode) {
          setSelectionMode(caps.selection_mode);
        }
        if (Array.isArray(caps?.skip_list)) {
          setSkipList(new Set(caps.skip_list));
        }
        if (Array.isArray(caps?.allowed_list)) {
          setAllowedList(new Set(caps.allowed_list));
        }
      } catch (err) {
        console.error('Error loading provider selection:', err);
      } finally {
        setLoaded(true);
      }
    };

    load();
  }, [merchantId, providerId]);

  // Perform the actual save via RPC
  const doSave = useCallback(async (pending: PendingSave) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { error } = await supabase.rpc('set_provider_selection', {
        p_merchant_id: pending.merchantId,
        p_provider_id: pending.providerId,
        p_selection_mode: pending.mode,
        p_skip_list: pending.skipList,
        p_allowed_list: pending.allowedList,
      });

      if (error) {
        console.error('Error saving provider selection:', error);
        setSaveError(error.message || 'Failed to save selection');
      } else {
        // Save succeeded — clear pending
        pendingSaveRef.current = null;
      }
    } catch (err) {
      console.error('Error saving provider selection:', err);
      setSaveError('Failed to save selection');
    } finally {
      setSaving(false);
    }
  }, []);

  // Debounced save to DB via RPC
  const scheduleSave = useCallback(
    (mode: SelectionMode, skip: Set<string>, allowed: Set<string>) => {
      if (!merchantId || !providerId) return;

      const pending: PendingSave = {
        merchantId,
        providerId,
        mode,
        skipList: Array.from(skip),
        allowedList: Array.from(allowed),
      };

      // Always update the ref so flush knows the latest state
      pendingSaveRef.current = pending;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(() => {
        doSave(pending);
      }, 1000);
    },
    [merchantId, providerId, doSave],
  );

  // Flush pending save on unmount and beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingSaveRef.current) {
        flushSaveSync(pendingSaveRef.current, accessTokenRef.current);
        pendingSaveRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Cancel the debounce timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      // Flush any pending save on unmount (in-app navigation)
      if (pendingSaveRef.current) {
        flushSaveSync(pendingSaveRef.current, accessTokenRef.current);
        pendingSaveRef.current = null;
      }
    };
  }, []);

  const isSelected = useCallback(
    (sku: string): boolean => {
      if (selectionMode === 'select_all') {
        return !skipList.has(sku);
      } else {
        return allowedList.has(sku);
      }
    },
    [selectionMode, skipList, allowedList],
  );

  const toggleProduct = useCallback(
    (sku: string) => {
      if (selectionMode === 'select_all') {
        setSkipList((prev) => {
          const next = new Set(prev);
          if (next.has(sku)) {
            next.delete(sku);
          } else {
            next.add(sku);
          }
          scheduleSave(selectionMode, next, allowedList);
          return next;
        });
      } else {
        setAllowedList((prev) => {
          const next = new Set(prev);
          if (next.has(sku)) {
            next.delete(sku);
          } else {
            next.add(sku);
          }
          scheduleSave(selectionMode, skipList, next);
          return next;
        });
      }
    },
    [selectionMode, skipList, allowedList, scheduleSave],
  );

  const toggleMode = useCallback(() => {
    const newMode: SelectionMode = selectionMode === 'select_all' ? 'deselect_all' : 'select_all';
    const emptySet = new Set<string>();
    setSelectionMode(newMode);
    setSkipList(emptySet);
    setAllowedList(emptySet);
    scheduleSave(newMode, emptySet, emptySet);
  }, [selectionMode, scheduleSave]);

  // Bulk select: mark all given SKUs as selected
  const selectProducts = useCallback(
    (skus: string[]) => {
      if (selectionMode === 'select_all') {
        setSkipList((prev) => {
          const next = new Set(prev);
          for (const sku of skus) next.delete(sku);
          scheduleSave(selectionMode, next, allowedList);
          return next;
        });
      } else {
        setAllowedList((prev) => {
          const next = new Set(prev);
          for (const sku of skus) next.add(sku);
          scheduleSave(selectionMode, skipList, next);
          return next;
        });
      }
    },
    [selectionMode, skipList, allowedList, scheduleSave],
  );

  // Bulk deselect: mark all given SKUs as deselected
  const deselectProducts = useCallback(
    (skus: string[]) => {
      if (selectionMode === 'select_all') {
        setSkipList((prev) => {
          const next = new Set(prev);
          for (const sku of skus) next.add(sku);
          scheduleSave(selectionMode, next, allowedList);
          return next;
        });
      } else {
        setAllowedList((prev) => {
          const next = new Set(prev);
          for (const sku of skus) next.delete(sku);
          scheduleSave(selectionMode, skipList, next);
          return next;
        });
      }
    },
    [selectionMode, skipList, allowedList, scheduleSave],
  );

  const selectionCount =
    selectionMode === 'select_all' ? skipList.size : allowedList.size;

  return {
    selectionMode,
    isSelected,
    toggleProduct,
    toggleMode,
    selectProducts,
    deselectProducts,
    saving,
    saveError,
    selectionCount,
    loaded,
  };
};
